/**
 * KB-3306 — per-field ownership state for the pro forms.
 *
 * Reads one `fieldStates/<kind>__<id>` doc (the record shape lives in
 * `scripts/camp-data/_shared/fieldPathLib.cjs` `assertFieldStateDoc`, prose in
 * `docs/camp-data/field-provenance-data-model.md` §5b) and exposes it to the form field labels via a
 * React context. Nothing in `src/` read a `fieldStates` doc before this ticket.
 *
 * ── The field-key vocabulary is PORTED, not re-invented ───────────────────────────────────────────
 * `encodeFieldPath` / `parseFieldPath` / `fieldKeyFor` are a byte-parity port of the CJS in
 * `scripts/camp-data/_shared/fieldPathLib.cjs` (§1). The three server writers key the log by the same
 * encoding; a client that named a field one byte differently would read the wrong record. Parity is
 * pinned in `campDataFieldState.fieldKey.test.ts` against the CJS on a key carrying `.` and `:`.
 * That CJS is a fenced file owned by KB-3474 — we do NOT import it here (the functions runtime cannot
 * reach a client bundle anyway); we port the pure encoding and pin it.
 *
 * ── The badge decision is PURE ────────────────────────────────────────────────────────────────────
 * `describeBadge(record, audience)` returns a descriptor (text, the actorId to resolve for `{name}`,
 * an optional hover) or `null` — no React, no I/O — so the copy matrix is unit-tested without a DOM.
 * `FieldOwnershipBadge` resolves the name and renders.
 */

import React, { createContext, useContext, useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, db } from "@/services/firebaseService";

// ── entity kinds ──────────────────────────────────────────────────────────────────────────────────
export type FieldEntityKind = "offer" | "campLocation" | "campOrganization";

/** The `fieldStates` doc id — `entityKeyFor(kind, id)` in the CJS. Must agree byte-for-byte. */
export function entityKeyFor(kind: string, id: string): string {
  return `${kind == null ? "" : kind}__${id == null ? "" : id}`;
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// FIELD PATHS — RFC 6901 pointer with the keyed-array extension (ported from fieldPathLib.cjs §1)
// ════════════════════════════════════════════════════════════════════════════════════════════════

// "~" escapes FIRST and unescapes LAST so a literal "~5" is never mis-read as "]". Order and codes are
// byte-identical to the CJS SEG_ESCAPES.
const SEG_ESCAPES: ReadonlyArray<readonly [string, string]> = [
  ["~", "~0"],
  ["/", "~1"],
  [":", "~2"],
  [".", "~3"],
  ["[", "~4"],
  ["]", "~5"],
];

function escapeSegment(s: string): string {
  let out = String(s);
  for (const [ch, code] of SEG_ESCAPES) out = out.split(ch).join(code);
  return out;
}
function unescapeSegment(s: string): string {
  let out = String(s);
  for (let i = SEG_ESCAPES.length - 1; i >= 0; i--) out = out.split(SEG_ESCAPES[i][1]).join(SEG_ESCAPES[i][0]);
  return out;
}

export type PathSegment = string | { key: string };

/** Encode segments into a pointer. Throws on a malformed segment (parity with the CJS). */
export function encodeFieldPath(segments: PathSegment[]): string {
  if (!Array.isArray(segments)) throw new Error("campDataFieldState: encodeFieldPath expects an array of segments");
  return segments
    .map((seg) => {
      if (seg && typeof seg === "object" && !Array.isArray(seg)) {
        if (!("key" in seg) || seg.key == null || String(seg.key) === "") {
          throw new Error(`campDataFieldState: keyed-array segment must be { key: <non-empty> } (got ${JSON.stringify(seg)})`);
        }
        return `/[key=${escapeSegment(seg.key)}]`;
      }
      if (typeof seg !== "string" || seg === "") {
        throw new Error(`campDataFieldState: path segment must be a non-empty string or { key } (got ${JSON.stringify(seg)})`);
      }
      return `/${escapeSegment(seg)}`;
    })
    .join("");
}

/** Parse a pointer back into segments (parity with the CJS). */
export function parseFieldPath(path: string): PathSegment[] {
  if (typeof path !== "string") throw new Error(`campDataFieldState: parseFieldPath expects a string (got ${JSON.stringify(path)})`);
  if (path === "") return [];
  if (path[0] !== "/") throw new Error(`campDataFieldState: a field path must start with "/" (got ${JSON.stringify(path)})`);
  return path
    .split("/")
    .slice(1)
    .map((raw) => {
      const m = /^\[key=(.*)\]$/.exec(raw);
      if (m) {
        const key = unescapeSegment(m[1]);
        if (key === "") throw new Error(`campDataFieldState: empty keyed-array key in path ${JSON.stringify(path)}`);
        return { key };
      }
      const seg = unescapeSegment(raw);
      if (seg === "") throw new Error(`campDataFieldState: empty path segment in ${JSON.stringify(path)}`);
      return seg;
    });
}

/** The Firestore-map-safe key for a path: the pointer without its leading "/", with "/" → ":". */
export function fieldKeyFor(path: string): string {
  if (typeof path !== "string" || (path !== "" && path[0] !== "/")) {
    throw new Error(`campDataFieldState: fieldKeyFor expects a pointer starting with "/" (got ${JSON.stringify(path)})`);
  }
  const body = path.startsWith("/") ? path.slice(1) : path;
  const rawSegments = body === "" ? [] : body.split("/");
  for (const seg of rawSegments) {
    if (seg.includes(":")) {
      throw new Error(`campDataFieldState: a raw ":" in a path segment collides with the fieldKey delimiter — build the path with encodeFieldPath: ${JSON.stringify(path)}`);
    }
  }
  const fieldKey = rawSegments.join(":");
  if (fieldKey.includes(".")) {
    throw new Error(`campDataFieldState: fieldKey must not contain "." (Firestore dotted-key trap): ${JSON.stringify(fieldKey)}`);
  }
  return fieldKey;
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// The doc + record shapes (the subset the badge / hint read)
// ════════════════════════════════════════════════════════════════════════════════════════════════

export interface FieldWinner {
  cid: string;
  sourceClass: string; // pipeline | staff | director | agent | script | legacy | unknown
  actorId: string;
  at: string;
}
export interface FieldBasis {
  cid: string;
  valueHash: string;
  judgedAt: string;
}
export interface FieldLock {
  holder: string;
  class: string; // director | staff | ...
  at: string;
  cid: string;
  reason?: string;
}
export interface FieldLatestPipeline {
  cid: string;
  valueHash: string;
  observedAt: string;
  runId?: string;
}
export interface FieldRecord {
  path: string;
  fieldClass: string;
  policy: "auto" | "review" | "locked";
  canonicalHash: string;
  winner: FieldWinner;
  policySetBy?: unknown;
  basis?: FieldBasis;
  lock?: FieldLock;
  rejected?: Record<string, unknown>;
  latestPipeline?: FieldLatestPipeline;
  pending?: unknown;
}
export interface FieldStateDoc {
  schemaVersion: string;
  entity: { kind: FieldEntityKind; id: string };
  orgId?: string;
  locationId?: string;
  campSlug?: string;
  vendorType?: string;
  contribRev?: number;
  fields?: Record<string, FieldRecord>;
}

/** One `fieldStates` read, tolerant of absence. Returns `null` when the doc does not exist. */
export async function fetchFieldState(kind: FieldEntityKind, id: string): Promise<FieldStateDoc | null> {
  if (!id) return null;
  const snap = await getDoc(doc(db, "fieldStates", entityKeyFor(kind, id)));
  if (!snap.exists()) return null;
  return snap.data() as FieldStateDoc;
}

/** A `fieldContributions/<cid>` row (only the fields the vendor hint reads). Admin-only per the rules. */
export interface FieldContributionRow {
  value?: unknown;
  valueHash?: string;
  runId?: string;
  observedAt?: string;
  [k: string]: unknown;
}

/** Lazily read one contribution row — the state doc holds only the pipeline value's hash, not its value. */
export async function fetchFieldContribution(cid: string): Promise<FieldContributionRow | null> {
  if (!cid) return null;
  const snap = await getDoc(doc(db, "fieldContributions", cid));
  if (!snap.exists()) return null;
  return snap.data() as FieldContributionRow;
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// getCampDataFieldHints — the DIRECTOR path to a vendor value (KB-3487)
// ════════════════════════════════════════════════════════════════════════════════════════════════
//
// A director can read neither `fieldContributions` (the value) nor `campDataOrgs` (the switch) — both are
// admin-only. So the director hint comes from the redacted `getCampDataFieldHints` callable, which resolves
// the switch and reads the value server-side and returns ONLY { fieldPath, value, firstSeenAt }. The
// FieldStateProvider calls this ONCE per form open for a director audience (never per field — ruling 6);
// staff keep the direct `fetchFieldContribution` read above unchanged.

/** One redacted vendor hint from the callable — the entire shape (no actorId, cid, source). */
export interface VendorFieldHint {
  fieldPath: string;
  value: unknown;
  firstSeenAt: string;
}

/** Fetch every eligible vendor hint for an entity in one call. Returns [] when the switch is off. */
export async function getCampDataFieldHints(entity: { kind: FieldEntityKind; id: string }): Promise<VendorFieldHint[]> {
  const functions = getFunctions(app, "us-east4");
  const fn = httpsCallable<{ entity: { kind: FieldEntityKind; id: string } }, { hints: VendorFieldHint[] }>(
    functions,
    "getCampDataFieldHints",
  );
  const res = await fn({ entity });
  return res.data?.hints ?? [];
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// Badge decision — PURE. Returns a descriptor or null.
// ════════════════════════════════════════════════════════════════════════════════════════════════
//
// Copy matrix (ticket §2, settled by dispatcher rulings 1/2/4/6, 2026-09-13):
//
//   STAFF audience (the six states, AC1):
//     1. Maintained by KidsBooked · updated {relative}          winner.sourceClass === "pipeline"
//        (hover: run id + time; a legacy pipeline winner has no run id → time only)
//     2. Approved by {name} {date}    staff winner, basis.valueHash === canonicalHash   (ruling 2)
//     3. Edited by {name} {date}      staff winner, otherwise                            (ruling 2)
//     4. Edited by the camp ({name}) {date} · locked   policy locked, lock.class director
//     5. Locked by {name} · vendor updates ignored     policy locked, lock.class staff   (ruling 4)
//     6. (legacy) render nothing                       winner.sourceClass === "legacy"   (ruling 1)
//   (operator-apply is a `users/<uid>` staff winner post-KB-3475 and renders via the staff branch —
//    the former ruling-6 `camp-data-operator-apply` script branch was removed when KB-3475 landed.)
//
//   DIRECTOR audience (KB-3318 — now MOUNTED on the welcome location form):
//     • Edited by you · vendor updates won't replace this   director-class LOCK whose holder is the
//                                                           current uid (`lock.holder === "users/"+uid`)
//     • Edited by the camp                                  director-class lock held by ANOTHER uid at
//                                                           the same camp — no "you", no name (ruling 1)
//     • Verified by KidsBooked {date}                       staff winner, basis === canonical
//     • Maintained by KidsBooked · updated {relative}       pipeline winner
//
// Holder match keys on the director-class LOCK, not a bare director winner: "Edited by you" requires
// `policy === "locked"` AND `lock.class === "director"` AND the holder uid to equal the viewer's
// (KB-3318 ruling 1). A fixture supplying only the matching uid would pass a holder-ignoring impl, so
// the mismatch case is pinned separately.
//
// Rendering notes: `{name}` is resolved from `actorId` (strip "users/"); a non-uid actorId is never a
// first name (VERDICT_BY "nicholas", "camp-data-operator-apply"), and a denied lookup must not print
// the uid. `policy === "locked"` is keyed on, NEVER `lock` presence — a stale lock survives
// `planCanonicalApply` (brief). Legacy is `sourceClass === "legacy"`, NOT `cid === "legacy"` — the
// backfill stamps `cid:"legacy"` on nearly every seeded record (ruling 1).

export type BadgeAudience = "staff" | "director";

export interface BadgeDescriptor {
  /**
   * The badge text. Contains zero or more literal placeholders resolved at render:
   *   `{name}`     — replaced with the resolved first name of `actorId` (or dropped if unresolvable);
   *   `{date}`     — replaced with `atIso` formatted as an absolute date;
   *   `{relative}` — replaced with `atIso` formatted as a relative time (clock-dependent).
   */
  text: string;
  /** The actorId (e.g. "users/<uid>" or a non-uid like "nicholas") to resolve for `{name}`; absent when no name. */
  actorId?: string;
  /** Tooltip text (pipeline run id + time). */
  hover?: string;
  /** The ISO timestamp the `{date}` / `{relative}` placeholder formats. */
  atIso?: string;
}

/** A uid we can resolve to a person is spelled `users/<uid>`; everything else is a system actor. */
export function actorUidOrNull(actorId: string | undefined): string | null {
  if (typeof actorId !== "string") return null;
  return actorId.startsWith("users/") ? actorId.slice("users/".length) : null;
}

function isBasisApproved(rec: FieldRecord): boolean {
  return !!rec.basis && rec.basis.valueHash === rec.canonicalHash;
}

/** Staff-audience descriptor for a record, or null when nothing should render. */
function describeStaffBadge(rec: FieldRecord): BadgeDescriptor | null {
  const w = rec.winner;
  if (!w) return null;
  if (w.sourceClass === "legacy") return null;

  if (rec.policy === "locked") {
    const holder = rec.lock?.holder || w.actorId;
    const at = rec.lock?.at || w.at;
    if (rec.lock?.class === "director") {
      return { text: `Edited by the camp ({name}) {date} · locked`, actorId: holder, atIso: at };
    }
    return { text: `Locked by {name} · vendor updates ignored`, actorId: holder };
  }

  if (w.sourceClass === "pipeline") {
    return { text: `Maintained by KidsBooked · updated {relative}`, hover: pipelineHover(rec), atIso: w.at };
  }

  if (w.sourceClass === "staff") {
    return isBasisApproved(rec)
      ? { text: `Approved by {name} {date}`, actorId: w.actorId, atIso: w.at }
      : { text: `Edited by {name} {date}`, actorId: w.actorId, atIso: w.at };
  }

  // agent, script, unknown, or an unlocked director winner: no ticket copy. (KB-3475 landed, so
  // operator-apply now records a `users/<uid>` staff winner and renders through the staff branch above;
  // the former ruling-6 `camp-data-operator-apply` script branch is dead and removed.)
  return null;
}

/** Director-audience descriptor. `currentUid` is the viewer's uid, for the holder match (KB-3318). */
function describeDirectorBadge(rec: FieldRecord, currentUid?: string | null): BadgeDescriptor | null {
  const w = rec.winner;
  if (!w) return null;
  if (w.sourceClass === "legacy") return null;

  // A director-class lock: "Edited by you" only when the current viewer holds it; another director's
  // lock at the same camp gets "Edited by the camp" — no name, no "you" (KB-3318 ruling 1). Keyed on
  // the director-class LOCK, never a bare director winner, so an unlocked winner has no false "you".
  if (rec.policy === "locked" && rec.lock?.class === "director") {
    const holderUid = actorUidOrNull(rec.lock.holder);
    if (currentUid && holderUid && holderUid === currentUid) {
      return { text: `Edited by you · vendor updates won't replace this` };
    }
    return { text: `Edited by the camp` };
  }
  if (w.sourceClass === "staff" && isBasisApproved(rec)) {
    return { text: `Verified by KidsBooked {date}`, atIso: w.at };
  }
  if (w.sourceClass === "pipeline") {
    return { text: `Maintained by KidsBooked · updated {relative}`, hover: pipelineHover(rec), atIso: w.at };
  }
  return null;
}

function pipelineHover(rec: FieldRecord): string | undefined {
  const lp = rec.latestPipeline;
  const at = lp?.observedAt || rec.winner?.at;
  if (!at) return undefined;
  // A legacy pipeline winner has no run id → time only (brief drift note).
  return lp?.runId ? `Run ${lp.runId} · ${at}` : `${at}`;
}

/**
 * The single entry point: describe the badge for a record and audience, or null. `currentUid` is the
 * viewer's uid — used only by the director branch for the "Edited by you" holder match (KB-3318).
 */
export function describeBadge(
  rec: FieldRecord | null | undefined,
  audience: BadgeAudience,
  currentUid?: string | null,
): BadgeDescriptor | null {
  if (!rec) return null;
  return audience === "director" ? describeDirectorBadge(rec, currentUid) : describeStaffBadge(rec);
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// Vendor-hint decision — PURE.
// ════════════════════════════════════════════════════════════════════════════════════════════════
//
// The hint appears (staff only) when the pipeline value differs from the canonical value and has not
// been rejected (AC3). The state doc holds no vendor VALUE — only `latestPipeline.{cid,valueHash}` —
// so the component lazily reads `fieldContributions/<latestPipeline.cid>` for the value.

/** True when a vendor value is on offer for this field (differs from canonical, not rejected). */
export function shouldShowVendorHint(rec: FieldRecord | null | undefined): boolean {
  if (!rec) return false;
  const lp = rec.latestPipeline;
  if (!lp || !lp.valueHash) return false;
  if (lp.valueHash === rec.canonicalHash) return false;
  const rejected = rec.rejected || {};
  return !Object.prototype.hasOwnProperty.call(rejected, lp.valueHash);
}

// Field paths where the form has no single input to receive a vendor value, so "Use vendor value" is
// hidden (the hint still shows the value + "Keep mine"). The offer form edits `priceOverride`, not
// `/priceRegular`; `/variationData` and the synthetic aftercare leaf are composite; a keyed element is
// a whole sub-object (brief). `/address` is nine sub-fields with no single input; `/startDate` and
// `/endDate` render through a composite date-range picker whose vendor value type is not a form value
// we can safely set here — offering a "Use" that cannot write is a control that cannot run.
const NO_DIRECT_INPUT_PATHS = new Set<string>([
  "/priceRegular",
  "/variationData",
  "/variationData/timings/afterCareWindow",
  "/address",
  "/startDate",
  "/endDate",
]);

/** Whether the "Use vendor value" action can be offered for this path. */
export function canUseVendorValue(fieldPath: string): boolean {
  if (NO_DIRECT_INPUT_PATHS.has(fieldPath)) return false;
  // A keyed-array element (`/variations/[key=..]`) is a whole sub-object with no single input.
  return !parseFieldPath(fieldPath).some((seg) => typeof seg === "object");
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// React context — FieldStateProvider / useFieldState
// ════════════════════════════════════════════════════════════════════════════════════════════════
//
// A hook cannot render a context, so the PROVIDER mounts in the form components (ManageLocationPage,
// OfferFormDialogController, EditOrganizationPage, AgeGroupVariationModal) and `useFieldState` reads
// it. One `fieldStates` read per form open (AC5): the provider fetches once on mount. A COPY passes
// `enabled={false}` (the source offer's locks must not show on a new session) → ZERO reads (AC5).

export interface FieldStateContextValue {
  doc: FieldStateDoc | null;
  loading: boolean;
  entity: { kind: FieldEntityKind; id: string } | null;
  /** Re-read the state doc — call after a lock / unlock / reject so the badges reflect the new state. */
  reload: () => void;
  /** True when a real provider is in the tree (a field rendered outside one shows no badge, never throws). */
  hasProvider: boolean;
  /**
   * The audience the enclosed badges render for (KB-3318). Defaults to "staff" — the admin forms mount
   * the provider with no audience and keep the staff matrix + lock control. The welcome flow mounts it
   * with "director" (or null, when the viewer's role has not resolved → no badge, no lock control).
   */
  audience: BadgeAudience | null;
  /** The viewer's uid, for the director "Edited by you" holder match (KB-3318). */
  currentUid: string | null;
  /**
   * Redacted vendor hints keyed by fieldPath, fetched ONCE per form open for a director audience (KB-3487).
   * Null for a staff audience (staff read the value directly via `fetchFieldContribution`). An empty map is
   * a resolved answer (switch off / no eligible field); null means not-a-director or not-yet-loaded.
   */
  hints: Record<string, VendorFieldHint> | null;
}

const EMPTY_CONTEXT: FieldStateContextValue = {
  doc: null,
  loading: false,
  entity: null,
  reload: () => {},
  hasProvider: false,
  audience: "staff",
  currentUid: null,
  hints: null,
};

const FieldStateContext = createContext<FieldStateContextValue>(EMPTY_CONTEXT);

export interface FieldStateProviderProps {
  kind: FieldEntityKind;
  id: string | null | undefined;
  /** When false (a copy, or an entity with no id yet), no read is made and no state is exposed. */
  enabled?: boolean;
  /** The audience the enclosed badges render for (default "staff"; null → render nothing). KB-3318. */
  audience?: BadgeAudience | null;
  /** The viewer's uid, for the director "Edited by you" holder match. KB-3318. */
  currentUid?: string | null;
  children: React.ReactNode;
}

export function FieldStateProvider({
  kind,
  id,
  enabled = true,
  audience = "staff",
  currentUid = null,
  children,
}: FieldStateProviderProps): React.ReactElement {
  const [doc, setDoc] = useState<FieldStateDoc | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  // Bumping this re-runs the fetch effect (a reload after a lock / unlock / reject write). It does NOT
  // change on a form open, so AC5's "one read per form open" holds — a reload is an explicit user act.
  const [reloadNonce, setReloadNonce] = useState<number>(0);
  const [hints, setHints] = useState<Record<string, VendorFieldHint> | null>(null);
  const active = enabled && !!id;

  useEffect(() => {
    if (!active || !id) {
      setDoc(null);
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    fetchFieldState(kind, id)
      .then((d) => {
        if (mounted) setDoc(d);
      })
      .catch(() => {
        // A denied/absent read is not an error worth surfacing here — the forms open normally with no
        // badges (AC5). The rules deny a director's read of an entity they cannot reach; staff (admin)
        // reads succeed.
        if (mounted) setDoc(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [kind, id, active, reloadNonce]);

  // Director-only: fetch the redacted vendor hints ONCE per form open (ruling 6 — never one call per
  // field). Staff read values directly via `fetchFieldContribution`, so this stays off their path and does
  // not touch the AC5 one-`fieldStates`-read discipline (the callable is not a `getDoc`). A denied or failed
  // call shows no hints rather than a broken form — never render an absence we did not verify.
  useEffect(() => {
    if (!active || !id || audience !== "director") {
      setHints(null);
      return;
    }
    let mounted = true;
    getCampDataFieldHints({ kind, id })
      .then((list) => {
        if (mounted) setHints(Object.fromEntries(list.map((h) => [h.fieldPath, h])));
      })
      .catch(() => {
        if (mounted) setHints(null);
      });
    return () => {
      mounted = false;
    };
  }, [kind, id, active, audience, reloadNonce]);

  const value: FieldStateContextValue = {
    doc,
    loading,
    entity: active && id ? { kind, id } : null,
    reload: () => setReloadNonce((n) => n + 1),
    hasProvider: true,
    audience,
    currentUid,
    hints,
  };
  return React.createElement(FieldStateContext.Provider, { value }, children);
}

export interface UseFieldStateResult {
  record: FieldRecord | null;
  doc: FieldStateDoc | null;
  entity: { kind: FieldEntityKind; id: string } | null;
  loading: boolean;
  reload: () => void;
  hasProvider: boolean;
  /** The audience the nearest provider set (default "staff"; null → render nothing). KB-3318. */
  audience: BadgeAudience | null;
  /** The viewer's uid from the nearest provider, for the holder match. KB-3318. */
  currentUid: string | null;
  /** The redacted vendor hint for this field (director audience only), or null. KB-3487. */
  hint: VendorFieldHint | null;
}

/** Read the per-field record for a pointer from the nearest FieldStateProvider (safe outside one). */
export function useFieldState(fieldPath: string): UseFieldStateResult {
  const ctx = useContext(FieldStateContext);
  let record: FieldRecord | null = null;
  if (ctx.doc && ctx.doc.fields) {
    try {
      record = ctx.doc.fields[fieldKeyFor(fieldPath)] || null;
    } catch {
      record = null; // a malformed pointer never crashes a form label
    }
  }
  return {
    record,
    doc: ctx.doc,
    entity: ctx.entity,
    loading: ctx.loading,
    reload: ctx.reload,
    hasProvider: ctx.hasProvider,
    audience: ctx.audience,
    currentUid: ctx.currentUid,
    hint: ctx.hints ? ctx.hints[fieldPath] ?? null : null,
  };
}

// ════════════════════════════════════════════════════════════════════════════════════════════════
// setCampDataFieldPolicy — the client wrapper for the lock / unlock / reject callable
// ════════════════════════════════════════════════════════════════════════════════════════════════

export interface SetFieldPolicyInput {
  entity: { kind: FieldEntityKind; id: string };
  fieldPath: string;
  action: "lock" | "unlock" | "reject";
  valueHash?: string;
  reason?: string;
}
export interface SetFieldPolicyResult {
  ok: true;
  action: "lock" | "unlock" | "reject";
  fieldKey: string;
}

export async function setCampDataFieldPolicy(input: SetFieldPolicyInput): Promise<SetFieldPolicyResult> {
  const functions = getFunctions(app, "us-east4");
  const fn = httpsCallable<SetFieldPolicyInput, SetFieldPolicyResult>(functions, "setCampDataFieldPolicy");
  const res = await fn(input);
  return res.data;
}
